import React from 'react';
import { MdModeEdit } from 'react-icons/md';
import styled from 'styled-components';
import { useAskVantage } from '../../../context/AskVantageProvider';
import { ActionType } from '../../../helpers/enums';
import { type ThemeConfig, useTheme } from '../../../theme';
import type { Action } from '../../../types/actions';
import type { ActionBlock as ActionBlockType } from '../../../types/blocks';
import Tooltip from '../../Tooltip';

interface FollowUpQuestionsActionProps {
  actions: Action['actions'];
}

const FollowUpQuestionsAction: React.FC<FollowUpQuestionsActionProps> = ({ actions }) => {
  const { send, thinkingState, fillMessageBox } = useAskVantage();
  const theme = useTheme();

  const handleActionClick = async (actionText: string) => {
    try {
      await send(actionText);
    } catch (err) {
      console.error('Failed to handle follow-up question:', err);
    }
  };

  const handleEditClick = (e: React.MouseEvent, actionText: string) => {
    e.stopPropagation();
    fillMessageBox(actionText);
  };

  if (!actions || actions.length === 0) {
    return null;
  }

  return (
    <FollowUpQuestionsWrapper>
      <ActionsList $theme={theme}>
        {actions.map((action) => (
          <ActionItem key={action.id} $theme={theme}>
            <ActionLink
              onClick={() => handleActionClick(action.text)}
              $theme={theme}
              disabled={Boolean(thinkingState)}
            >
              {action.text}
            </ActionLink>
            <Tooltip content="Edit before sending" position="bottom">
              <EditIconButton
                className="edit-icon"
                onClick={(e) => handleEditClick(e, action.text)}
                disabled={Boolean(thinkingState)}
                $theme={theme}
              >
                <MdModeEdit size={12} />
              </EditIconButton>
            </Tooltip>
          </ActionItem>
        ))}
      </ActionsList>
    </FollowUpQuestionsWrapper>
  );
};

interface RenderActionProps {
  action: Action;
}

const RenderAction: React.FC<RenderActionProps> = ({ action }) => {
  switch (action.action_type) {
    case ActionType.FOLLOW_UP_QUESTIONS:
      return <FollowUpQuestionsAction actions={action.actions} />;
  }
};

interface ActionBlockProps {
  block: ActionBlockType;
}

const ActionBlock = ({ block }: ActionBlockProps) => {
  return (
    <Wrapper>
      <RenderAction action={block} />
    </Wrapper>
  );
};

export default ActionBlock;

const Wrapper = styled.div`
  margin-top: 12px;
`;

const FollowUpQuestionsWrapper = styled.div`
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
  padding: 0 24px;
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: 768px) {
    padding: 0 16px;
    padding-bottom: 12px;
  }
`;

const ActionsList = styled.div<{ $theme: ThemeConfig }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  align-items: flex-end;
  margin-top: 8px;
`;

const ActionItem = styled.div<{ $theme: ThemeConfig }>`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;

  &:hover .edit-icon {
    opacity: 1;
    visibility: visible;
  }
`;

const ActionLink = styled.button<{ $theme: ThemeConfig }>`
  background: transparent;
  border: none;
  color: #2b7bc5;
  cursor: pointer;
  font-size: 12px;
  text-align: right;
  transition: all 0.2s ease;
  line-height: 1.4;
  padding: 0;
  text-decoration: none;

  &:hover:not(:disabled) {
    text-decoration: underline;
    color: #236ba3;
  }

  &:active:not(:disabled) {
    color: #1f5a8a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const EditIconButton = styled.button<{ $theme: ThemeConfig }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px !important;
  width: 32px !important;
  height: 32px !important;
  background: ${(props) => props.$theme.colors.messageBoxBg};
  border: none;
  border-radius: 50%;
  color: #90bfa0;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  box-shadow: 0px 4px 4px 0px #0000000d;

  &:hover:not(:disabled) {
    background: ${(props) => props.$theme.colors.quickPromptBg};
    color: #90bfa0;
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    opacity: 1;
    visibility: visible;
  }
`;
